import React, { useState, useCallback } from 'react';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import userService from '../../services/userService';
import serviceProviderService from '../../services/serviceProviderService';
import debounce from 'lodash/debounce';

const UserSelection = ({ 
    selectedUserType, 
    selectedUsers, 
    onUserTypeChange, 
    onUsersChange,
    isSubmitting,
    errors,
    isMulti = true,
    placeholder = "Select users...",
    className = 'mb-3'
}) => {
    // User type options
    const userTypeOptions = [
        { value: 'user', label: 'Users' },
        { value: 'service_provider', label: 'Service Providers' }
    ];

    // Handle user type change
    const handleUserTypeChange = (newUserType) => {
        // Clear selected users when user type changes
        onUsersChange([]);
        // Call the original onUserTypeChange
        onUserTypeChange(newUserType);
    };

    // Debounced load options function
    const debouncedLoadOptions = useCallback(
        debounce(async (inputValue, callback) => {
            // Don't make API call if input is less than 2 characters
            if (!inputValue || inputValue.length < 2) {
                callback([]);
                return;
            }

            try {
                let response;
                if (selectedUserType?.value === 'user') {
                    response = await userService.getList({ search: inputValue });
                } else {
                    response = await serviceProviderService.getList({ search: inputValue });
                }

                const options = response.data.map(item => ({
                    value: item.id,
                    label: item.name
                }));
                callback(options);
            } catch (error) {
                console.error('Error loading options:', error);
                callback([]);
            }
        }, 500), // 500ms debounce
        [selectedUserType]
    );

    // Wrapper function for AsyncSelect
    const loadOptions = (inputValue, callback) => {
        debouncedLoadOptions(inputValue, callback);
    };

    return (
        <>
            {/* User Type Selection */}
            <div className={`${className}`}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    User Type
                </label>
                <Select
                    value={selectedUserType}
                    onChange={handleUserTypeChange}
                    options={userTypeOptions}
                    className={`basic-single ${errors.user_type ? 'is-invalid' : ''}`}
                    classNamePrefix="select"
                    placeholder="Select user type..."
                    isDisabled={isSubmitting}
                    isClearable
                />
                {errors.user_type && (
                    <div className="invalid-feedback">
                        {Array.isArray(errors.user_type) ? errors.user_type[0] : errors.user_type}
                    </div>
                )}
            </div>

            {/* User Selection */}
            {selectedUserType && (
                <div className={`${className}`}>
                    {selectedUserType.label && (
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {selectedUserType.label}
                        </label>
                    )}
                    <AsyncSelect
                        key={selectedUserType.value} // This will force re-render and clear cache when user type changes
                        value={selectedUsers}
                        onChange={onUsersChange}
                        loadOptions={loadOptions}
                        className={`basic-multi-select ${errors.user_ids ? 'is-invalid' : ''}`}
                        classNamePrefix="select"
                        isMulti={isMulti}
                        placeholder={placeholder}
                        isClearable
                        cacheOptions
                        defaultOptions={false}
                        isDisabled={isSubmitting}
                        loadingMessage={() => "Loading..."}
                        noOptionsMessage={({ inputValue }) => 
                            !inputValue || inputValue.length < 2 
                                ? "Type at least 2 characters..." 
                                : "No options found"
                        }
                    />
                    {errors.user_ids && (
                        <div className="invalid-feedback">
                            {Array.isArray(errors.user_ids) ? errors.user_ids[0] : errors.user_ids}
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default UserSelection;
